import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from './useAuth';

interface LedgerEventPayload {
  id: string;
  company_id: string;
  shareholder_id: string;
  event_type: string;
  shares: number;
  created_by: string;
  share_class?: string;
}

export function useCapTableNotifications(companyId?: string) {
  const { user } = useAuth();

  useEffect(() => {
    if (!companyId || !user) return;

    const channel = supabase
      .channel(`cap-table-changes-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ledger_events',
          filter: `company_id=eq.${companyId}`,
        },
        (payload: any) => {
          const newEvent = payload.new as LedgerEventPayload;
          const oldEvent = payload.old as LedgerEventPayload;

          // Don't show notifications for changes made by the current user
          if (newEvent?.created_by === user.id) {
            return;
          }

          // Handle different event types
          if (payload.eventType === 'INSERT') {
            const eventTypeLabel = getEventTypeLabel(newEvent.event_type);
            toast.info(`Cap table updated: ${eventTypeLabel}`, {
              description: `${Math.abs(newEvent.shares).toLocaleString()} ${newEvent.share_class || 'Ordinary'} shares`,
              duration: 5000,
            });
          } else if (payload.eventType === 'UPDATE') {
            toast.info('Cap table entry updated', {
              description: 'A ledger event has been modified by another user',
              duration: 5000,
            });
          } else if (payload.eventType === 'DELETE') {
            toast.info('Cap table entry removed', {
              description: 'A ledger event has been deleted by another user',
              duration: 5000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, user]);
}

function getEventTypeLabel(eventType: string): string {
  const labels: Record<string, string> = {
    ISSUANCE: 'New shares issued',
    TRANSFER: 'Shares transferred',
    BUYBACK: 'Share buyback',
    SPLIT: 'Stock split',
    CONSOLIDATION: 'Share consolidation',
    ADJUSTMENT: 'Share adjustment',
    EXERCISE: 'Option exercise',
  };
  return labels[eventType] || eventType;
}
